<template>
  <main class="file-info">
    <section
      v-if="selectedFile && checkValidFileType(selectedFile)"
      class="preview"
      @click="previewFile"
      @mouseenter="setPreviewHovered(true)"
      @mouseleave="setPreviewHovered(false)"
    >
      <button
        v-if="previewHovered && fileBlobSource && !showPreviewFailedText && !loadingFile"
        class="preview__button"
      >
        <IconExternalLink class="preview__button__icon" />
      </button>
      <VuePdfEmbed
        v-if="fileBlobSource && !loadingFile && fileType === 'pdf'"
        class="preview__pdf"
        :class="{ preview__pdf__hover: previewHovered }"
        :page="1"
        :source="fileBlobSource"
        :width="700"
      />
      <img
        v-if="fileBlobSource && !loadingFile && fileType !== 'pdf'"
        class="preview__image"
        :class="{ preview__image__hover: previewHovered }"
        :src="fileBlobSource"
        :alt="t('general__text__preview-click-to')"
      />
      <p
        v-if="showPreviewFailedText && !fileBlobSource && !loadingFile"
        class="preview__error-text"
      >
        {{ t('general__text__preview-failed') }}
      </p>
      <div v-if="loadingFile" class="preview__loading-spinner">
        <PulseLoader color="#3f46d6" size="16px" />
      </div>
    </section>
    <section class="info">
      <table>
        <tbody>
          <tr v-if="actCategoryName.length">
            <td>{{ t('general__text__category') }}</td>
            <td>
              {{ actCategoryName }}
            </td>
          </tr>
          <tr v-if="actTypeName.length">
            <td>{{ t('general__text__type') }}</td>
            <td>
              {{ actTypeName }}
            </td>
          </tr>
          <tr v-if="actStatusName.length">
            <td>{{ t('general__text__status') }}</td>
            <td>
              {{ actStatusName }}
            </td>
          </tr>
          <tr>
            <td>{{ t('general__text__uploaded') }}</td>
            <td>
              {{
                selectedFile?.createdDate ? new Date(selectedFile.createdDate).toLocaleString() : ''
              }}
            </td>
          </tr>
          <tr>
            <td>{{ t('general__text__uploaded-by') }}</td>
            <td>
              {{ selectedFile?.uploadedBy ?? '' }}
            </td>
          </tr>
          <tr>
            <td>{{ t('general__text__file-type') }}</td>
            <td>{{ selectedFile?.fileType ?? '' }}</td>
          </tr>
          <tr>
            <td>{{ t('general__text__size') }}</td>
            <td>
              {{
                selectedFile?.fileSize ? formatNumber(selectedFile.fileSize / 1000, 0) + ' kB' : ''
              }}
            </td>
          </tr>
          <tr>
            <td>{{ t('general__text__placement') }}</td>
            <BadgeSquare
              v-if="selectedFile?.entityId && parentNames[selectedFile?.entityId]"
              :badgeText="parentNames[selectedFile?.entityId]"
              :grey="true"
              size="extra-small"
            />
          </tr>
        </tbody>
      </table>
    </section>
    <br />
    <section v-if="documentMetadata || imageMetadata" class="info">
      <p class="info__heading">
        {{ `${t('general__text__additional')} ${t('general__text__information').toLowerCase()}` }}
      </p>
      <table>
        <tr v-if="documentMetadata?.lastModified">
          <td>{{ t('general__text__last-modified-date') }}</td>
          <td>
            {{ new Date(documentMetadata.lastModified).toLocaleString() }}
          </td>
        </tr>
        <tr v-if="documentMetadata?.createdDate">
          <td>{{ t('general__text__created-date') }}</td>
          <td>
            {{ new Date(documentMetadata.createdDate).toLocaleString() }}
          </td>
        </tr>
        <tr v-if="documentMetadata?.lastAuthor">
          <td>
            {{ `${t('general__text__last-modified')} ${t('general__text__by').toLowerCase()}` }}
          </td>
          <td>
            {{ documentMetadata.lastAuthor }}
          </td>
        </tr>
        <tr v-if="documentMetadata?.creator">
          <td>
            {{ `${t('general__text__created')} ${t('general__text__by').toLowerCase()}` }}
          </td>
          <td>
            {{ documentMetadata.creator }}
          </td>
        </tr>
        <tr v-if="documentMetadata?.nrOfPages">
          <td>{{ t('general__text__pages-number-of') }}</td>
          <td>
            {{ documentMetadata.nrOfPages }}
          </td>
        </tr>
        <tr v-if="imageMetadata?.width">
          <td>{{ t('general__text__width') }}</td>
          <td>
            {{ imageMetadata?.width }}
          </td>
        </tr>
        <tr v-if="imageMetadata?.height">
          <td>{{ t('general__text__height') }}</td>
          <td>
            {{ imageMetadata?.height }}
          </td>
        </tr>
        <!-- due to serialization issues on backend, check for both lower and upper case R -->
        <tr v-if="imageMetadata?.xresolution">
          <td>{{ t('general__text__resolution-horizontal') }}</td>
          <td>
            {{ imageMetadata?.xresolution }}
          </td>
        </tr>
        <tr v-if="imageMetadata?.xResolution">
          <td>{{ t('general__text__resolution-horizontal') }}</td>
          <td>
            {{ imageMetadata?.xResolution }}
          </td>
        </tr>
        <!-- due to serialization issues on backend, check for both lower and upper case R -->
        <tr v-if="imageMetadata?.yresolution">
          <td>{{ t('general__text__resolution-vertical') }}</td>
          <td>
            {{ imageMetadata?.yresolution }}
          </td>
        </tr>
        <tr v-if="imageMetadata?.yResolution">
          <td>{{ t('general__text__resolution-vertical') }}</td>
          <td>
            {{ imageMetadata?.yResolution }}
          </td>
        </tr>
        <tr v-if="imageMetadata?.lastModified">
          <td>{{ t('general__text__last-modified-date') }}</td>
          <td>
            {{ new Date(imageMetadata.lastModified).toLocaleString() }}
          </td>
        </tr>
      </table>
    </section>
    <footer class="actions">
      <TooltipStandardV2
        :text="t('general__text__download')"
        :arrowPosition="'bottom-center'"
        :verticalOffset="2"
      >
        <ButtonOutline
          class="button"
          :iconLeft="downloadSmall"
          :size="'small'"
          @onClicked="emits('downloadFile')"
        />
      </TooltipStandardV2>
      <TooltipStandardV2
        v-if="store.getters.hasFileEditorAccess"
        :text="t('general__text__archive')"
        :arrowPosition="'bottom-center'"
        :verticalOffset="2"
      >
        <ButtonOutline
          class="button"
          :iconLeft="archiveSmall"
          :size="'small'"
          @onClicked="emits('archiveFile')"
        />
      </TooltipStandardV2>
      <TooltipStandardV2
        v-if="store.getters.hasFileAdminAccess"
        :text="t('general__text__delete')"
        :arrowPosition="'bottom-center'"
        :verticalOffset="2"
      >
        <ButtonOutline
          class="button"
          :iconLeft="trash2Small"
          :size="'small'"
          @onClicked="emits('deleteFile')"
        />
      </TooltipStandardV2>
    </footer>
  </main>
</template>
<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useGeneralStore } from '@/pinia'
import { useCoreEntityStore } from '@/pinia/coreEntity'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { formatNumber } from '@/utils/math'
import { Attachment } from '@/services/attachments.service'
import { getAttachmentFileBlob } from '@/services/files.service'
import BadgeSquare from '@/design-system/atoms/BadgeSquare.vue'
import ButtonOutline from '@/design-system/atoms/buttons/ButtonOutline.vue'
import IconExternalLink from '@/design-system/assets/icons/IconExternalLink.vue'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import VuePdfEmbed from 'vue-pdf-embed'
import TooltipStandardV2 from '@/design-system/atoms/TooltipStandardV2.vue'
import { getLineLayerById } from '@/services/lines.service'
import { getZoneLayerById } from '@/services/zones.service'
import { getPoiById } from '@/services/pois.service'
import { getRealmId } from '@/utils/realmId'
import { getActCategoryById } from '@/services/acts.service'
import downloadSmall from '@/design-system/assets/icons/Icon-Download-small.svg'
import archiveSmall from '@/design-system/assets/icons/Icon-Archive-Small.svg'
import trash2Small from '@/design-system/assets/icons/Icon-Trash-2-small.svg'
import * as Sentry from '@sentry/vue'

const piniaStore = useGeneralStore()
const coreEntityStore = useCoreEntityStore()
const store = useStore()
const { t } = useI18n()

const emits = defineEmits(['archiveFile', 'deleteFile', 'downloadFile'])
const selectedFile = computed(() => {
  return piniaStore.selectedFile
})

const actCategoryName = ref('')
const actTypeName = ref('')
const actStatusName = ref('')
const coreEntityTypes = ['COMPLEX', 'REAL_ESTATE', 'CONSTRUCTION', 'LEVEL', 'SPACE']
const fileBlobSource = ref('')
const fileType = ref<string | undefined>('')
const loadingFile = ref(false)
const parentNames = ref<Record<string, string>>({})
const previewHovered = ref(false)
const showPreviewFailedText = ref(false)

const documentMetadata = computed(() => selectedFile.value?.metadata?.documentMetadata)
const imageMetadata = computed(() => selectedFile.value?.metadata?.imageMetadata)

function checkValidFileType(item: Attachment) {
  return (
    item.fileType === 'gif' ||
    item.fileType === 'jpg' ||
    item.fileType === 'jpeg' ||
    item.fileType === 'pdf' ||
    item.fileType === 'png' ||
    item.fileType === 'svg'
  )
}

async function getEntityParents(file: Attachment): Promise<string | undefined> {
  try {
    if (!file.entityId) return undefined
    let parents
    if (coreEntityTypes.includes(file.entityType)) {
      const currentEntity = await coreEntityStore.getCoreEntity(file.entityId)
      const parentNames = await coreEntityStore.getParents(file.entityId)
      parents = [...parentNames, currentEntity]
    } else if (file.entityType === 'POI') {
      const poi = await getPoiById(file.entityId)
      if (!poi) return undefined
      const currentEntity = await coreEntityStore.getCoreEntity(poi.coreId)
      const parentNames = await coreEntityStore.getParents(poi.coreId)
      parents = [...parentNames, currentEntity, poi]
    } else if (file.entityType === 'ZONE') {
      const zone = await getZoneLayerById(file.entityId)
      if (!zone) return undefined
      const currentEntity = await coreEntityStore.getCoreEntity(zone.coreId)
      const parentNames = await coreEntityStore.getParents(zone.coreId)
      parents = [...parentNames, currentEntity, zone]
    } else if (file.entityType === 'LINE') {
      const line = await getLineLayerById(file.entityId)
      if (!line) return undefined
      const currentEntity = await coreEntityStore.getCoreEntity(line.coreId)
      const parentNames = await coreEntityStore.getParents(line.coreId)
      parents = [...parentNames, currentEntity, line]
    } else {
      // case realm
      parents = [{ name: getRealmId() }]
    }
    if (!parents) return undefined
    const parentNames = [...parents].map((parent) => parent.name).join(', ')
    return parentNames
  } catch (error) {
    Sentry.captureException(error)
    return undefined
  }
}
async function getPreviewFile() {
  if (!piniaStore.selectedFile?.storageName || !piniaStore.selectedFile.realmId) return
  try {
    fileType.value = piniaStore.selectedFile.fileType ?? ''
    loadingFile.value = true
    const res = await getAttachmentFileBlob({
      realmId: piniaStore.selectedFile.realmId,
      storageName: piniaStore.selectedFile.storageName
    })
    if (!res) {
      showPreviewFailedText.value = true
      return
    }
    const blob =
      fileType?.value === 'svg'
        ? new Blob([res.data], { type: 'image/svg+xml' })
        : new Blob([res.data])
    fileBlobSource.value = window.URL.createObjectURL(blob)
    showPreviewFailedText.value = false
  } catch (e) {
    showPreviewFailedText.value = true
    console.error(e)
  } finally {
    loadingFile.value = false
  }
}
function previewFile() {
  if (showPreviewFailedText.value || loadingFile.value) return
  piniaStore.setCurrentOverlayModalPinia('previewFilePinia')
  piniaStore.setShowOverlayModalPinia(true)
}
function resetActData() {
  actCategoryName.value = ''
  actTypeName.value = ''
  actStatusName.value = ''
}
function setPreviewHovered(value: boolean) {
  if (showPreviewFailedText.value || loadingFile.value) previewHovered.value = false
  else previewHovered.value = value
}
watch(
  selectedFile,
  async (newFile) => {
    resetActData()
    if (newFile && checkValidFileType(newFile)) getPreviewFile()
    if (!newFile?.entityId) return
    const parents = await getEntityParents(newFile)
    if (!parents) return
    parentNames.value[newFile.entityId] = parents
    if (newFile.actCategory) {
      const actCategory = await getActCategoryById(newFile.actCategory)
      if (!actCategory) return
      actCategoryName.value = actCategory.name
      actTypeName.value = actCategory.types.find((type) => type.id === newFile?.actType)?.name ?? ''
      actStatusName.value =
        actCategory.statuses.find((status) => status.id === newFile?.actStatus)?.name ?? ''
    }
  },
  { immediate: true }
)
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/mixins.scss';
.file-info {
  align-items: center;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
  @include mixins.scrollbar-vertical;
  p {
    color: variables.$color-greyscale-secondary;
    margin: 8px 4px;
    @include font-mixins.desktop-regular-tiny;
  }
  .preview {
    align-items: center;
    display: flex;
    justify-content: center;
    max-height: 60%;
    margin-bottom: 16px;
    position: relative;
    text-align: center;
    z-index: 1;
    &__button {
      all: unset;
      height: 40px;
      position: absolute;
      width: 40px;
      z-index: 2;
      &__icon {
        color: variables.$color-pure-white;
        cursor: pointer;
      }
    }
    &__image {
      height: intrinsic; // Possibly solves stretched image issue in Safari
      margin: auto;
      max-height: 100%;
      max-width: 374px;
      object-fit: contain;
      width: 100%;
      @include font-mixins.desktop-regular-paragraph;

      &__hover {
        background-color: rgba(#1a1a1a, 0.5);
        cursor: pointer;
        filter: brightness(50%);
      }
    }
    &__loading-spinner {
      margin: 56px auto;
    }
    &__pdf {
      max-height: 100%;
      max-width: 374px;
      object-fit: contain;
      overflow: hidden;
      padding-bottom: 4px; // Necessary for correct scroll bar activation
      @include mixins.scrollbar-vertical;
      &__hover {
        background-color: rgba(#1a1a1a, 0.5);
        cursor: pointer;
        filter: brightness(50%);
      }
    }
    &__error-text {
      color: variables.$color-error;
      margin: 32px 0 48px 0;
      min-width: 300px;
      @include font-mixins.desktop-regular-paragraph;
    }
  }
  .info {
    width: 100%;
    &__heading {
      color: variables.$color-greyscale-content;
      @include font-mixins.desktop-bold-label;
    }
    table {
      border-collapse: collapse;
      width: 100%;
      tr {
        display: flex;
        justify-content: space-between;
        align-items: center;
        height: 28px;
        &:not(:last-child) {
          border-bottom: 0.5px solid variables.$color-greyscale-line-grey;
        }
        td {
          color: variables.$color-greyscale-content;
          padding: 8px 4px;
          @include font-mixins.desktop-bold-tiny;
          &:first-child {
            color: variables.$color-greyscale-secondary;
            width: 121px;
            @include font-mixins.desktop-regular-tiny;
          }
        }
      }
    }
  }
  .actions {
    align-items: flex-end;
    display: flex;
    gap: 16px;
    height: 40px;
    justify-content: center;
    margin-top: auto;
    padding-top: 8px;
    .button {
      width: 40px;
    }
  }
}
</style>
